import React, { useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Container,
  Typography, Box, AppBar, Toolbar, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GenerateFee from './GenerateFee';
import DepositFee from './DepositFee';
import FeeDiscountApprovalReport from './FeeDiscountApprovalReport';

// ── User Guide content for the Payment Report page ────────────────────────
const paymentUserGuideSteps = [
  {
    section: "📋 Fee Generate Report",
    steps: [
      { label: "Academic Session", desc: "Select one or multiple academic/financial year sessions. The most recent session is auto-selected by default. Use 'Select All' to include all sessions." },
      { label: "Month", desc: "Choose one or more months to filter fee records. Use 'Select All' to include every month in the report." },
      { label: "Class Type", desc: "Optionally filter by class type (e.g. Primary, Secondary). Leave as 'All Class Types' to include every type." },
      { label: "Class", desc: "Pick a specific class to narrow down results. Leave as 'All Classes' to include every class." },
      { label: "Section", desc: "Select a section after choosing a class. This field is disabled until a class is selected." },
      { label: "Student", desc: "Select a specific student after choosing a section. This field is disabled until a section is selected." },
      { label: "Payment Status", desc: "Filter records by payment status — All (default), Paid only, or Unpaid only." },
      { label: "Rows Per Page", desc: "Control how many records appear per page. Options: 50, 100, or 200 rows." },
      { label: "Generate Report", desc: "Click to fetch and display the fee report based on all selected filters." },
      { label: "Download Excel", desc: "Click to export the filtered report data as an Excel (.xlsx) file to your device." },
      { label: "Balance Column", desc: "Shown in Red when there is an outstanding balance due. Shown in Green when the balance is fully cleared." },
      { label: "Charge Breakup", desc: "Hover over the truncated text in this column to see the full item-wise charge breakdown in a tooltip." },
    ],
  },
  {
    section: "💰 Fee Deposit",
    steps: [
      { label: "Class Type Dropdown", desc: "Select a class type to narrow the student list (e.g. Regular, Management). Optional filter." },
      { label: "All Class / Class Wise Student", desc: "Choose 'All Class' to include every class, or 'Class Wise Student' to pick a specific class from the dropdown that appears." },
      { label: "All Section / Select Section", desc: "Choose 'All Section' for every section, or 'Select Section' to pick a specific one from the dropdown." },
      { label: "All Student / Select Student", desc: "Choose 'All Student' to include everyone, or 'Select Student' to pick an individual student." },
      { label: "From Date & To Date", desc: "Set the payment date range. Defaults to the 1st of the current month through today. Adjust to filter a specific time period." },
      { label: "Fee Deposit Report", desc: "Click to open a detailed printable report in a new browser tab, showing all transactions within the selected date range." },
      { label: "Download CSV", desc: "Click to download the current fee deposit data as a CSV file compatible with Excel and other tools." },
    ],
  },
];

// ── Payment User Guide Modal ───────────────────────────────────────────────
const PaymentUserGuide = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      },
    }}
  >
    {/* ── Gradient header — matches screenshot style ── */}
    <DialogTitle
      sx={{
        background: "linear-gradient(135deg, #1a237e 0%, #1976d2 50%, #26c6da 100%)",
        color: "#fff",
        fontWeight: 700,
        fontSize: "1.15rem",
        textAlign: "center",
        py: 2.5,
        letterSpacing: "0.04em",
      }}
    >
      📘 Payment Report — User Guide
    </DialogTitle>

    {/* ── Scrollable content ── */}
    <DialogContent
      dividers
      sx={{
        px: 3,
        py: 2.5,
        maxHeight: "62vh",
        overflowY: "auto",
        backgroundColor: "#fafafa",
      }}
    >
      {paymentUserGuideSteps.map((group, gi) => (
        <Box key={gi} sx={{ mb: gi < paymentUserGuideSteps.length - 1 ? 3 : 0 }}>

          {/* Section heading — teal/blue like screenshot */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.97rem",
              color: "#1565c0",
              mb: 1.5,
              pb: 0.5,
              borderBottom: "2px solid #bbdefb",
            }}
          >
            {group.section}
          </Typography>

          {/* Step list */}
          {group.steps.map((step, si) => (
            <Box
              key={si}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                mb: 1.2,
              }}
            >
              {/* Bullet dot */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#1976d2",
                  mt: "5px",
                  flexShrink: 0,
                }}
              />

              {/* Step text */}
              <Typography sx={{ fontSize: "0.875rem", color: "#333", lineHeight: 1.6 }}>
                {/* Bold label like screenshot */}
                <Box component="span" sx={{ fontWeight: 700, color: "#111" }}>
                  {step.label}
                </Box>
                {" — "}
                {step.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}

      {/* Bottom tip */}
      <Box
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: "8px",
          backgroundColor: "#e3f2fd",
          border: "1px solid #90caf9",
        }}
      >
        <Typography sx={{ fontSize: "0.8rem", color: "#1565c0", fontStyle: "italic" }}>
          💡 <strong>Tip:</strong> Always select <strong>Academic Session</strong> and <strong>Month</strong> first before generating the Fee Generate Report for best results.
        </Typography>
      </Box>
    </DialogContent>

    {/* ── Close button ── */}
    <DialogActions sx={{ justifyContent: "center", py: 2, backgroundColor: "#fafafa" }}>
      <Button
        onClick={onClose}
        variant="contained"
        sx={{
          background: "linear-gradient(45deg, #1976d2 30%, #26c6da 90%)",
          borderRadius: "25px",
          px: 5,
          py: 1,
          fontWeight: 600,
          textTransform: "none",
          fontSize: "0.9rem",
          boxShadow: "0 3px 8px rgba(25,118,210,0.35)",
          "&:hover": {
            background: "linear-gradient(45deg, #1565c0 30%, #00acc1 90%)",
          },
        }}
      >
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

// ── Main Payment Component ─────────────────────────────────────────────────
function Payment() {
  // Controls the User Guide modal open/close
  const [guideOpen, setGuideOpen] = useState(false);

  return (
    <Container>

      {/* ── AppBar with ℹ button pinned to the right ── */}
      <AppBar position="static" style={{ backgroundColor: "#0B1F40" }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

          {/* Page title */}
          <Typography variant="h4" component="div" style={{ fontWeight: "bold" }}>
            Payment Report
          </Typography>

          {/* ── ℹ Info button — opens User Guide modal ── */}
          <IconButton
            onClick={() => setGuideOpen(true)}
            size="small"
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.15)",
              border: "2px solid rgba(255,255,255,0.7)",
              color: "#fff",
              fontWeight: 900,
              fontSize: "1rem",
              fontFamily: "serif",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.3)",
                border: "2px solid #fff",
                transform: "scale(1.12)",
                boxShadow: "0 0 12px rgba(255,255,255,0.4)",
              },
            }}
          >
            i
          </IconButton>

        </Toolbar>
      </AppBar>

      {/* ── User Guide Modal ── */}
      <PaymentUserGuide open={guideOpen} onClose={() => setGuideOpen(false)} />

      {/* ── Fee Generate Report Accordion ── */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography variant="h6">Fee Generate Report</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <GenerateFee />
        </AccordionDetails>
      </Accordion>

      {/* Add another Accordion item for Fee Deposit */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel2a-content"
          id="panel2a-header"
        >
          <Typography variant="h6">Fee Deposit</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Call a different component inside this Accordion item */}
          <DepositFee />
        </AccordionDetails>
      </Accordion>


       {/* Add another Accordion item for Fee Discount Approval Report */}
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel3a-content"
          id="panel3a-header"
        >
          <Typography variant="h6">Fee Discount Approval Report</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FeeDiscountApprovalReport />
        </AccordionDetails>
      </Accordion>
      

    </Container>
  );
}

export default Payment;